import {
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import ModalMAIQ from 'app/components/ModalMAIQ';
import CopyToClipboard from 'app/components/CopyToClipboard';
import CellChipStatus from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/components/cells/CellChipStatus';
import { formatToUtcTime } from 'utils/dateUtils';

import type { ExecutionLogRow } from '../types';

type ExecutionLogDetailModalProps = {
  open: boolean;
  row: ExecutionLogRow | null;
  onClose: () => void;
};

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function DetailField({
  label,
  value,
  copyable = false,
  monospace = false,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  monospace?: boolean;
}) {
  const displayValue = value || '—';
  const canCopy = copyable && displayValue !== '—';

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.2 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            wordBreak: 'break-all',
            fontFamily: monospace ? 'monospace' : 'inherit',
            fontSize: monospace ? '0.8rem' : '0.875rem',
            lineHeight: 1.5,
            flex: 1,
          }}
        >
          {displayValue}
        </Typography>
        {canCopy ? <CopyToClipboard text={displayValue} /> : null}
      </Box>
    </Box>
  );
}

export default function ExecutionLogDetailModal({
  open,
  row,
  onClose,
}: ExecutionLogDetailModalProps) {
  if (!row) return null;

  const createdAt = row.created_at
    ? formatToUtcTime(row.created_at)
  : '—';
  const reason = formatValue(row.reason_for_the_status);
  const status = formatValue(row.maiq_lead_status);

  const identifierFields: Array<{
    key: keyof ExecutionLogRow;
    label: string;
    copyable?: boolean;
  }> = [
    { key: '_id', label: 'Log Id', copyable: true },
    { key: 'lead_id', label: 'Lead Id', copyable: true },
    { key: 'shopper_id', label: 'Shopper Id', copyable: true },
    { key: 'opportunity_id', label: 'Opportunity Id', copyable: true },
  ];

  const runFields: Array<{
    key: keyof ExecutionLogRow;
    label: string;
    copyable?: boolean;
  }> = [
    { key: 'run_id', label: 'Run Id', copyable: true },
    { key: 'action_id', label: 'Action Id', copyable: true },
    { key: 'segment_id', label: 'Segment Id', copyable: true },
    { key: 'dealer_id', label: 'Dealer Id', copyable: true },
  ];

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton>
      <Paper
        elevation={0}
        sx={{
          width: 720,
          maxWidth: '92vw',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        <Box
          className="modal-header"
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon className="modal-header-icon" fontSize="small" />
            <Typography variant="h6" fontWeight={600}>
              Execution Log Details
            </Typography>
          </Box>
          <Tooltip title="Close">
            <IconButton
              className="modal-header-button"
              aria-label="close"
              onClick={onClose}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ overflowY: 'auto', p: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                mb: reason !== '—' ? 1.5 : 0,
              }}
            >
              {status !== '—' ? <CellChipStatus>{status}</CellChipStatus> : null}
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" fontWeight={600} color="text.primary">
                  Created:
                </Typography>{' '}
                {createdAt}
              </Typography>
            </Box>
            {reason !== '—' ? (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                >
                  Reason For The Status
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {reason}
                </Typography>
              </Box>
            ) : null}
          </Paper>

          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 700, color: 'text.primary' }}
          >
            Identifiers
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {identifierFields.map(field => (
              <Grid item xs={12} sm={6} key={field.key}>
                <DetailField
                  label={field.label}
                  value={formatValue(row[field.key])}
                  copyable={field.copyable}
                  monospace
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 700, color: 'text.primary' }}
          >
            Run &amp; Segment
          </Typography>
          <Grid container spacing={2}>
            {runFields.map(field => (
              <Grid item xs={12} sm={6} key={field.key}>
                <DetailField
                  label={field.label}
                  value={formatValue(row[field.key])}
                  copyable={field.copyable}
                  monospace
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
}
